type OptionalString = string | undefined;
type OptionalNumber = number | undefined;

class IsbnUtils {
  ISBN_10_REGEX = /^([0-9]{9})[0-9xX]$/;
  ISBN_13_REGEX = /^([0-9]{12})[0-9xX]$/;

  /**
   * Cleans up potential ISBNs to form normalised ISBN10/13 values.
   *
   * @param {string} isbn - A potential ISBN number
   * @returns {?string} - A cleaned ISBN10/13, if one can be derived.
   **/
  cleanup(isbn): OptionalString {
    const matches = isbn.match(/([0-9Xx\s\-]*)/);
    if (matches && matches.length >= 2) {
      const cleanedIsbn = matches[1].replace(/([^0-9Xx]*)/g, '');
      if (
        this.isCleanedIsbn10(cleanedIsbn) ||
        this.isCleanedIsbn13(cleanedIsbn)
      ) {
        return cleanedIsbn.toUpperCase();
      }
      return undefined;
    }
    return undefined;
  }

  /**
   * Convert an array of ISBNs into an array of unique, normalised ISBN13s
   * Invalid ISBNs are discarded
   *
   * @param {string[]} isbns - An array of ISBNs
   * @returns {string[]} - An array of unique valid ISBN13s
   */
  isbnsToUniqueIsbn13s(isbns: string[]) {
    const isbn13s = isbns
      .map(isbn => {
        if (this.isIsbn13(isbn)) {
          return this.cleanup(isbn);
        } else if (this.isIsbn10(isbn)) {
          return this.isbn10ToIsbn13(isbn);
        }

        return undefined;
      })
      .filter(isbn => isbn);

    return Array.from(new Set([...isbn13s]));
  }

  /**
   * Convert an ISBN10 into an ISBN13.
   *
   * @param {string} isbn10 - A potential ISBN10 number
   * @returns {?string} - A cleaned ISBN13, if one can be derived.
   **/
  isbn10ToIsbn13(isbn10): OptionalString {
    const cleanedIsbn10 = this.cleanup(isbn10);
    if (this.isCleanedIsbn10(cleanedIsbn10)) {
      const [, firstNineDigits] = cleanedIsbn10.match(this.ISBN_10_REGEX);
      const prefixedIsbn10 = `978${firstNineDigits}`;
      const checkDigit = this.computeCheckDigitIsbn13(prefixedIsbn10);
      return `${prefixedIsbn10}${checkDigit}`;
    }
    return undefined;
  }

  /**
   * Compute the check-digit for an ISBN13.
   *
   * @param {?string} isbn13 - A cleaned ISBN13 number
   * @returns {?string} - The check-digit value for an ISBN13.
   **/
  computeCheckDigitIsbn13(isbn13): OptionalNumber {
    if (isbn13) {
      const chars = isbn13.split('');

      // sum digits, alternating multipliers of 1 (even) and 3 (odd)
      let sumOfDigits = 0;
      for (let i = 0; i < chars.length; i++) {
        const digit = Number(chars[i]);
        const multiplier = i % 2 === 1 ? 3 : 1;
        sumOfDigits += digit * multiplier;
      }

      // divide the sumOfDigits by the modulus number (10) to find the remainder
      // and then minus 10 to get the check digit
      const checkDigit = (10 - (sumOfDigits % 10)) % 10;

      return checkDigit;
    }
    return undefined;
  }

  /**
   * Checks whether an ISBN10 can be recovered from a string.
   *
   * @param {string} isbn - A potential ISBN10 number
   * @returns {boolean} - true if a valid ISBN10 can be derived from `isbn`
   **/
  isIsbn10(isbn): boolean {
    return this.isCleanedIsbn10(this.cleanup(isbn));
  }

  /**
   * Checks whether a string is a valid, normalised ISBN10.
   *
   * @param {string} isbn - A potential ISBN10 number
   * @returns {boolean} - true if `isbn` is a valid, normalised ISBN10
   **/
  isCleanedIsbn10(cleanedIsbn10): boolean {
    return cleanedIsbn10 && cleanedIsbn10.match(this.ISBN_10_REGEX) && true;
  }

  /**
   * Checks whether an ISBN13 can be recovered from a string.
   *
   * @param {string} isbn - A potential ISBN13 number
   * @returns {boolean} - true if a valid ISBN13 can be derived from `isbn`
   **/
  isIsbn13(isbn): boolean {
    return this.isCleanedIsbn13(this.cleanup(isbn));
  }

  /**
   * Checks whether a string is a valid, normalised ISBN13.
   *
   * @param {string} isbn - A potential ISBN13 number
   * @returns {boolean} - true if `isbn` is a valid, normalised ISBN13
   **/
  isCleanedIsbn13(cleanedIsbn13): boolean {
    return cleanedIsbn13 && cleanedIsbn13.match(this.ISBN_13_REGEX) && true;
  }
}

export default new IsbnUtils();
